<template>
  <v-dialog v-model="isOpen" max-width="480">
    <v-card v-if="account" data-cy="welcome-email-modal">
      <v-card-title class="pa-4">
        {{ t("message.WelcomeEmail.title") }}
      </v-card-title>
      <v-card-text class="pa-4">
        <div class="mb-4">
          <div class="text-caption text-grey">
            {{
              account.kind === "district"
                ? t("message.WelcomeEmail.districtLabel")
                : t("message.WelcomeEmail.schoolLabel")
            }}
          </div>
          <div class="text-body-1" data-cy="welcome-email-name">{{ account.name || "-" }}</div>
        </div>
        <div class="mb-4">
          <div class="text-caption text-grey">
            {{ t("message.WelcomeEmail.contactLabel") }}
          </div>
          <div class="text-body-2" data-cy="welcome-email-contact">
            {{ account.contactName || "-" }}
          </div>
        </div>
        <div class="mb-4">
          <div class="text-caption text-grey">
            {{ t("message.WelcomeEmail.emailLabel") }}
          </div>
          <div class="text-body-2" data-cy="welcome-email-email">
            {{ account.contactEmail || "-" }}
          </div>
        </div>
        <div class="mb-4">
          <div class="text-caption text-grey">
            {{ t("message.WelcomeEmail.subdomainLabel") }}
          </div>
          <div class="text-body-2" data-cy="welcome-email-subdomain">
            {{ account.subdomain || "-" }}
          </div>
        </div>
        <div class="mb-4">
          <div class="text-caption text-grey">
            {{ t("message.WelcomeEmail.usernameLabel") }}
          </div>
          <div class="text-body-2" data-cy="welcome-email-username">
            {{ account.username || "-" }}
          </div>
        </div>
        <div class="text-body-2 text-grey">
          {{ t("message.WelcomeEmail.body") }}
        </div>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          variant="outlined"
          :disabled="saving"
          data-cy="welcome-email-cancel-btn"
          @click="close"
        >
          {{ t("message.WelcomeEmail.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="saving"
          data-cy="welcome-email-confirm-btn"
          @click="send"
        >
          {{ t("message.WelcomeEmail.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useAlertsStore } from "@/stores/alerts";
import useSchools from "@/stores/support/schools";
import type { WelcomeEmailAccount } from "@/components/support/welcomeEmail";

const props = defineProps<{
  modelValue: boolean;
  account: WelcomeEmailAccount | null;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

const { t } = useI18n();
const alertsStore = useAlertsStore();
const schoolStore = useSchools();
const saving = ref(false);

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const account = computed(() => props.account);

const close = () => {
  isOpen.value = false;
};

const send = async () => {
  if (!props.account) return;
  saving.value = true;
  try {
    const result = await schoolStore.sendWelcomeEmail({
      kind: props.account.kind,
      id: props.account.id,
    });
    if (result.status) {
      alertsStore.addAlert("success", t("message.WelcomeEmail.success"));
      close();
    } else {
      alertsStore.addAlert("error", result.message || t("message.WelcomeEmail.error"));
    }
  } catch (error) {
    console.error("Error sending welcome email:", error);
    alertsStore.addAlert("error", t("message.WelcomeEmail.error"));
  } finally {
    saving.value = false;
  }
};
</script>

<style scoped lang="scss">
.text-grey {
  color: rgba(0, 0, 0, 0.6);
}
</style>
